import type { ReactNode } from 'react';
import { color, stageTheme, formTheme } from '../styles/theme';
import { shortDate } from '../lib/format';
import { statusLabelOf } from '../lib/pipeline';
import { messageOne } from '../lib/messageTypes';
import type { MetaResponse, Person } from '../api/client';
import { contactStageLabel, type Contact, type ContactRecord, type ContactStage } from '../api/contacts';

/**
 * The pieces a person is described by on the Contacts screen and in their
 * record: their stage, a tag per record, and the flags that must never be
 * missed. Shared so the list and the record say the same thing the same way.
 * The owner pickers' list of people is here too, for the same reason: a
 * contact's Owner and a record's Owner offer the same people.
 */

type Tone = { bg: string; fg: string };

/**
 * A stage's colour. The journey is blue until it pays off (green), endings
 * are grey, a clinician's ladder is purple like the rest of the partner-side
 * work, and anything waiting on someone is amber.
 */
const STAGE_TONE: Record<ContactStage, Tone> = {
  interested: stageTheme.new,
  applied: stageTheme.new,
  screening: stageTheme.new,
  enrolled: stageTheme.done,
  completed: stageTheme.done,
  'on-hold': stageTheme.active,
  'not-a-match': stageTheme.closed,
  withdrawn: stageTheme.closed,
  closed: stageTheme.closed,
  asking: formTheme.partnership,
  referring: formTheme.partnership,
  'opted-out': stageTheme.closed,
  'open-request': stageTheme.active,
  answered: stageTheme.closed,
  subscriber: stageTheme.closed,
  unsubscribed: stageTheme.closed,
  none: stageTheme.closed,
};

export function StagePill({ stage }: { stage: ContactStage }) {
  const t = STAGE_TONE[stage];
  return (
    <span
      style={{
        display: 'inline-block',
        background: t.bg,
        color: t.fg,
        fontSize: 11.5,
        fontWeight: 800,
        padding: '4px 10px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
      }}
    >
      {contactStageLabel[stage]}
    </span>
  );
}

/** A record's tag colour: live work blue, waiting amber, finished grey. */
const GROUP_TONE: Record<ContactRecord['group'], Tone> = {
  open: stageTheme.new,
  parked: stageTheme.active,
  closed: stageTheme.closed,
};

/**
 * A record in the words the other screens use for it: "Transdiagnostic ·
 * Screening" for a study board, "OUD · Waitlist" for the inbox, "Media
 * inquiry · Received" for a message, "Referral · OUD · Enrolled" for a
 * patient a clinician referred.
 */
export function recordLabel(meta: MetaResponse, r: ContactRecord): string {
  const status = statusLabelOf(meta, r.status);
  if (r.area === 'message') return `${messageOne(meta, r.form)} · ${status}`;
  const where = r.study?.short ?? meta.forms[r.form]?.short ?? r.form;
  if (r.area === 'referral') return `Referral · ${where} · ${status}`;
  return `${where} · ${status}`;
}

/** The longer reading, for a hover: where it lives and how it got there. */
export function recordTitle(meta: MetaResponse, r: ContactRecord): string {
  const status = statusLabelOf(meta, r.status);
  const where = r.study?.label ?? meta.forms[r.form]?.label ?? r.form;
  const place =
    r.area === 'inbox'
      ? 'waiting in Forms submission'
      : r.area === 'study'
        ? 'on the study board'
        : r.area === 'referral'
          ? 'a patient they referred'
          : 'in Messages';
  const extra = [
    r.via === 'referred' ? 'referred by a clinician' : null,
    r.endorsedFrom ? `endorsed from ${r.endorsedFrom.short}` : null,
  ].filter(Boolean);
  return `${where}: ${status}, ${place}${extra.length ? ` (${extra.join('; ')})` : ''}. Updated ${shortDate(r.updatedAt)}.`;
}

/**
 * Records that say the same thing, as one: eight "Study opening alert ·
 * Received" forms are one line with a count, not eight. The first of each
 * group is the newest, as the API orders them.
 */
export function groupRecords(records: readonly ContactRecord[]): Array<{ first: ContactRecord; count: number }> {
  const groups = new Map<string, { first: ContactRecord; count: number }>();
  for (const r of records) {
    const key = [r.area, r.form, r.study?.key ?? '', r.status, r.via].join('|');
    const held = groups.get(key);
    if (held) held.count += 1;
    else groups.set(key, { first: r, count: 1 });
  }
  return [...groups.values()];
}

/** A status on its own, in its record's colour, for where the name is already said. */
export function StatusPill({ meta, record }: { meta: MetaResponse; record: ContactRecord }) {
  const t = GROUP_TONE[record.group];
  return (
    <span
      style={{
        display: 'inline-block',
        background: t.bg,
        color: t.fg,
        fontSize: 11,
        fontWeight: 800,
        padding: '3px 9px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
      }}
    >
      {statusLabelOf(meta, record.status)}
    </span>
  );
}

export function RecordTag({
  meta,
  record,
  count = 1,
  onOpen,
}: {
  meta: MetaResponse;
  record: ContactRecord;
  /** How many records this one tag stands for. */
  count?: number;
  onOpen?: () => void;
}) {
  const t = GROUP_TONE[record.group];
  const style = {
    display: 'inline-block',
    maxWidth: '100%',
    background: t.bg,
    color: t.fg,
    fontSize: 11,
    fontWeight: 800,
    padding: '3px 9px',
    borderRadius: 999,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    border: 'none',
    cursor: onOpen ? 'pointer' : 'default',
  } as const;
  const label = `${recordLabel(meta, record)}${count > 1 ? ` ×${count}` : ''}`;
  const title = count > 1 ? `${count} of these. The newest: ${recordTitle(meta, record)}` : recordTitle(meta, record);
  return onOpen ? (
    <button onClick={onOpen} title={`${title} Open it.`} style={style}>
      {label}
    </button>
  ) : (
    <span title={title} style={style}>
      {label}
    </span>
  );
}

/** A staff tag: outlined, so it never reads as a status the system set. */
export function StaffTag({ children, onRemove }: { children: ReactNode; onRemove?: () => void }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        border: `1px solid ${color.line}`,
        background: color.white,
        color: color.ink,
        fontSize: 11,
        fontWeight: 700,
        padding: '2px 9px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
      {onRemove && (
        <button
          onClick={onRemove}
          aria-label="Remove this tag"
          title="Remove this tag"
          style={{ background: 'transparent', border: 'none', padding: 0, color: color.muted, cursor: 'pointer', fontSize: 12, lineHeight: 1 }}
        >
          ×
        </button>
      )}
    </span>
  );
}

/** The duplicate flag rides on a tag matching sets; it is a flag, not a tag. */
export const DUPLICATE_TAG = 'possible-duplicate';

/**
 * What must not be missed about a person, beside their name: they asked us
 * to stop, or they may be someone we already have.
 */
export function ContactFlags({ contact }: { contact: Pick<Contact, 'suppressedAt' | 'suppressionSource' | 'tags'> }) {
  return (
    <>
      {contact.suppressedAt && (
        <span
          title={
            contact.suppressionSource === 'person'
              ? 'They asked us themselves to stop contacting them'
              : contact.suppressionSource === 'staff'
                ? 'Staff marked them do-not-contact'
                : 'Do not contact them'
          }
          style={flag('#f8e3dd', color.red)}
        >
          Do not contact
        </span>
      )}
      {contact.tags.includes(DUPLICATE_TAG) && (
        <span
          title="Matching found another record with this phone number. Check whether they are the same person; if not, choose Not a duplicate on their record."
          style={flag(stageTheme.active.bg, stageTheme.active.fg)}
        >
          Possible duplicate
        </span>
      )}
    </>
  );
}

function flag(bg: string, fg: string) {
  return {
    flex: 'none',
    background: bg,
    color: fg,
    fontSize: 10,
    fontWeight: 800,
    padding: '2px 7px',
    borderRadius: 999,
    whiteSpace: 'nowrap',
  } as const;
}

/**
 * The people an owner picker offers: every active account, by name, from
 * `/admins/people`, which any signed-in role may read — so the picker works
 * for whoever may assign, not only for a role that reads Admin access. The
 * caller adds its own first options ("Unassigned", "anyone").
 *
 * An owner who is no longer active is not in that list; they stay as an
 * option of their own, marked, so the select still says who holds the work
 * instead of falling back to its first line.
 */
export function PeopleOptions({
  people,
  current,
}: {
  people: readonly Person[] | undefined;
  current?: { id: string | null | undefined; name: string | null | undefined };
}) {
  const list = people ?? [];
  const id = current?.id ?? null;
  const missing = id !== null && !list.some((p) => p.id === id);
  return (
    <>
      {missing && (
        <option value={id}>
          {current?.name ?? 'Someone'}
          {people ? ' · no longer active' : ''}
        </option>
      )}
      {list.map((p) => (
        <option key={p.id} value={p.id}>
          {p.name}
        </option>
      ))}
    </>
  );
}
